import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck,  RefreshCw, X, Check, Trash2, AlertCircle,
  Search, Package, TrendingUp, AlertTriangle, CheckCircle2,
  Info, CheckCircle, XCircle, FilterX, Calendar,
  Printer, FileText,  ShieldCheck, Eye,
   ChevronRight, 
  Layers, Boxes, ChevronDown, Zap, ListChecks, ArrowRightCircle,
  Ban, FlaskConical, PackageCheck, Scale, Percent,
  Route,  ArrowRightLeft, Wand2, Settings2,
} from 'lucide-react';
import apiClient from '../api/client';
import DimassiLogo from '../assets/Dimassi_logo.png';
import DashboardMenuHeader from '../components/AlmacenMenuHeader';
import MultiSelectFilter from '../components/MultiSelectFilter';
import './DashboardBultos.css';
import './Pedidos.css';
import './PanelAlmacen.css';
import './DashboardInventario.css';

/* ============================================================
   HELPERS
   ============================================================ */
const MESES = [
  'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
];

const HOY = new Date();
const MES_ACTUAL = String(HOY.getMonth() + 1).padStart(2, '0');
const ANIO_ACTUAL = String(HOY.getFullYear());

const formatNumber = (v, d = 0) => Number(v || 0).toLocaleString('es-VE', {
  minimumFractionDigits: d, maximumFractionDigits: d,
});
const formatUSD = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(v || 0));
const formatDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('es-VE', { dateStyle: 'short' });
};

const aniosDisponibles = () => {
  const y = Number(ANIO_ACTUAL);
  return [String(y), String(y - 1), String(y - 2)];
};

const ultimoDiaDelMes = (anio, mes) => new Date(Number(anio), Number(mes), 0).getDate();

const propuestaToData = (p, overrides = {}) => ({
  ciudad: p.ciudad_principal,
  camion: overrides.camion || p.camion_sugerido,
  ruta: p.ruta,
  ruta_orden: p.ruta_orden,
  color: p.color,
  rutas_involucradas: p.rutas_involucradas || [p.ruta],
  es_inter_ruta: !!p.es_inter_ruta,
  totales: {
    bultos: p.total_bultos,
    peso: p.total_peso_kg,
    m3: p.total_m3,
    usd: p.total_usd,
  },
  minimoUSD: p.minimo_usd,
  porcentajeFlete: p.porcentaje_flete,
  costoFlete: p.monto_flete_usd,
  grupo_marca: p.grupo_marca,
  utilizacion_max_pct: p.utilizacion_max_pct,
  notas: p.notas.map(n => ({
    nota_num: n.nota_num,
    cliente_principal: n.cliente_principal,
    cliente_nombre: n.cliente_nombre,
    direccion_entrega: n.direccion_entrega,
    cantidad: n.cantidad,
    peso: n.peso,
    m3: n.m3,
    total_usd: n.total_usd,
    marcas: n.marcas,
    ciudad: n.ciudad,
  })),
});

/* ============================================================
   TOAST
   ============================================================ */
const Toast = ({ show, type, message, onClose }) => {
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [show, onClose]);

  if (!show) return null;

  const config = {
    success: { icon: CheckCircle, class: 'toast-success' },
    error: { icon: XCircle, class: 'toast-error' },
    info: { icon: Info, class: 'toast-info' },
  };
  const { icon: Icon, class: cls } = config[type] || config.info;

  return (
    <div className={`pc-toast ${cls}`} onClick={onClose}>
      <Icon size={18} />
      <span>{message}</span>
      <button onClick={onClose}><X size={14} /></button>
    </div>
  );
};

/* ============================================================
   CONFIRM DIALOG
   ============================================================ */
const ConfirmDialog = ({ show, title, message, onConfirm, onCancel, loading }) => {
  if (!show) return null;
  return (
    <div className="pc-modal-overlay" onClick={onCancel}>
      <div className="pc-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="pc-confirm-icon"><AlertTriangle size={32} /></div>
        <h3>{title || '¿Estás seguro?'}</h3>
        <p>{message}</p>
        <div className="pc-confirm-actions">
          <button onClick={onCancel} disabled={loading}>Cancelar</button>
          <button onClick={onConfirm} className="danger" disabled={loading}>
            {loading ? 'Procesando…' : <><Check size={14} /> Confirmar</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   BADGE DE GRUPO DE MARCA
   ============================================================ */
const GRUPO_CONFIG = {
  PEROS: { label: 'PEROS', icon: PackageCheck, cls: 'grupo-peros' },
  ASPEROX: { label: 'ASPEROX', icon: FlaskConical, cls: 'grupo-asperox' },
  GENERAL: { label: 'GENERAL', icon: Layers, cls: 'grupo-general' },
};

const BadgeGrupoMarca = ({ grupo }) => {
  const cfg = GRUPO_CONFIG[grupo] || GRUPO_CONFIG.GENERAL;
  const Icon = cfg.icon;
  return (
    <span className={`pc-grupo-chip ${cfg.cls}`} title={`Grupo de marca: ${cfg.label}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
};

/* ============================================================
   MODAL RESOLVER EXCESO DE CAPACIDAD
   ============================================================ */
const ModalOpcionesExcede = ({ show, propuesta, onClose, onDividir, onCambiarCamion, onNotify }) => {
  const [tab, setTab] = useState('cambiar');
  const [analisis, setAnalisis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [camionSel, setCamionSel] = useState(null);

  useEffect(() => {
    if (!show || !propuesta) return;
    let cancelado = false;
    const cargar = async () => {
      setLoading(true); setError('');
      try {
        const res = await apiClient.post('/almacen/consolidacion/analizar', {
          notas_nums: propuesta.notas.map(n => n.nota_num),
          ciudad: propuesta.ciudad_principal,
          camion_id: propuesta.camion_sugerido?.id || null,
        });
        if (cancelado) return;
        if (res.data?.success) {
          setAnalisis(res.data.data);
          const fit = res.data.data.camiones_analisis.find(c => c.fit_90)
            || res.data.data.camiones_analisis.find(c => c.fit_100);
          setCamionSel(fit || null);
        } else {
          setError(res.data?.detail || 'Error al analizar');
        }
      } catch (e) {
        if (!cancelado) setError(e.response?.data?.detail || 'Error al analizar');
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    cargar();
    return () => { cancelado = true; };
  }, [show, propuesta]);

  if (!show || !propuesta) return null;

  const handleDividir = () => {
    const div = analisis?.division_sugerida;
    if (!div || div.length < 2) {
      onNotify?.('error', 'La división no es posible (una nota sola ya excede el camión)');
      return;
    }
    if (div.some(d => d.excede)) {
      onNotify?.('error', 'Al menos un sub-viaje sigue excediendo — revisa las notas problemáticas');
      return;
    }
    onDividir(div.map(d => ({
      ciudad_principal: propuesta.ciudad_principal,
      notas: d.notas,
      camion_sugerido: d.camion_sugerido,
    })));
    onClose();
  };

  const handleCambiar = () => {
    if (!camionSel) { onNotify?.('error', 'Selecciona un camión'); return; }
    onCambiarCamion({
      camion: {
        id: camionSel.id, tipo: camionSel.tipo,
        capacidad_kg: camionSel.capacidad_kg,
        capacidad_m3: camionSel.capacidad_m3,
      },
    });
    onClose();
  };

  const camionesDisp = analisis?.camiones_analisis || [];
  const fit90 = camionesDisp.filter(c => c.fit_90);
  const fit100 = camionesDisp.filter(c => c.fit_100 && !c.fit_90);
  const noFit = camionesDisp.filter(c => !c.fit_100);
  const divisionOk = (analisis?.division_sugerida || []).length > 1
    && !analisis.division_sugerida.some(d => d.excede);

  return (
    <div className="pc-modal-overlay" onClick={onClose}>
      <div className="pc-modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div className="pc-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Scale size={18} className="pc-icon-danger" />
            <h3>Resolver exceso de capacidad</h3>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>

        <div className="pc-modal-body">
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <RefreshCw size={20} className="spin-anim" />
              <p style={{ marginTop: 8, opacity: 0.7 }}>Analizando opciones…</p>
            </div>
          ) : error ? (
            <div className="pc-form-error"><AlertCircle size={14} /> {error}</div>
          ) : (
            <>
              <div className="pc-excede-resumen">
                <div>
                  <span className="lbl">Carga actual</span>
                  <strong>{formatNumber(analisis.totales.peso, 2)} kg · {formatNumber(analisis.totales.m3, 3)} m³</strong>
                </div>
                <div>
                  <span className="lbl">Camión sugerido</span>
                  <strong>{propuesta.camion_sugerido?.tipo || '—'} ({propuesta.utilizacion_max_pct}%)</strong>
                </div>
                <div>
                  <span className="lbl">Valor</span>
                  <strong>{formatUSD(analisis.totales.usd)}</strong>
                </div>
              </div>

              {analisis.notas_problematicas.length > 0 && (
                <div className="pc-excede-alerta-roja">
                  <AlertTriangle size={14} />
                  <div>
                    <strong>{analisis.notas_problematicas.length} nota(s) exceden el camión más grande incluso solas</strong>
                    <ul style={{ margin: '4px 0 0 16px', fontSize: '0.78rem' }}>
                      {analisis.notas_problematicas.map(n => (
                        <li key={n.nota_num}>
                          <strong>{n.nota_num}</strong> — {n.cliente_principal} · {formatNumber(n.peso, 2)} kg ({n.excede_pct}% del camión máx.)
                        </li>
                      ))}
                    </ul>
                    <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                      Estas notas deben dividirse a nivel de producto o reasignarse manualmente.
                    </span>
                  </div>
                </div>
              )}

              <div className="pc-excede-tabs">
                <button
                  className={`pc-excede-tab ${tab === 'cambiar' ? 'active' : ''}`}
                  onClick={() => setTab('cambiar')}>
                  <Truck size={13} /> Cambiar camión
                  {fit90.length > 0 && <span className="pc-excede-tab-badge green">{fit90.length}</span>}
                </button>
                <button
                  className={`pc-excede-tab ${tab === 'dividir' ? 'active' : ''}`}
                  onClick={() => setTab('dividir')}>
                  <Layers size={13} /> Dividir en viajes
                  {divisionOk && <span className="pc-excede-tab-badge green">{analisis.division_sugerida.length}</span>}
                </button>
              </div>

              {tab === 'cambiar' && (
                <div className="pc-excede-camiones">
                  {fit90.length === 0 && fit100.length === 0 && (
                    <div className="pc-excede-vacio">
                      <AlertCircle size={16} />
                      <span>Ningún camión disponible puede con esta carga. Usa la pestaña <strong>Dividir</strong>.</span>
                    </div>
                  )}
                  {[...fit90, ...fit100].map(c => (
                    <label
                      key={c.id}
                      className={`pc-excede-camion-row ${camionSel?.id === c.id ? 'selected' : ''} ${c.fit_90 ? 'ok' : 'warn'}`}>
                      <input
                        type="radio"
                        name="camion"
                        checked={camionSel?.id === c.id}
                        onChange={() => setCamionSel(c)} />
                      <Truck size={14} />
                      <span className="camion-tipo">{c.tipo}</span>
                      <span className="camion-cap">
                        {formatNumber(c.capacidad_kg)} kg · {formatNumber(c.capacidad_m3)} m³
                      </span>
                      <span className={`camion-util ${c.fit_90 ? 'ok' : 'warn'}`}>
                        {c.util_max_pct}%
                      </span>
                      {c.fit_90 && <span className="camion-badge ok">RECOMENDADO</span>}
                      {!c.fit_90 && c.fit_100 && <span className="camion-badge warn">90%–100%</span>}
                    </label>
                  ))}
                  {noFit.length > 0 && (
                    <div className="pc-excede-info-muted">
                      {noFit.length} camión(es) más pequeños no pueden con esta carga.
                    </div>
                  )}
                </div>
              )}

              {tab === 'dividir' && (
                <div className="pc-excede-division">
                  {!divisionOk ? (
                    <div className="pc-excede-vacio">
                      <AlertCircle size={16} />
                      <span>
                        La división automática no pudo resolver el exceso
                        {analisis.notas_problematicas.length > 0
                          ? ' (hay notas individuales demasiado grandes).'
                          : '.'}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="pc-excede-info-verde">
                        <CheckCircle2 size={14} />
                        <span>
                          Se puede dividir en <strong>{analisis.division_sugerida.length} viajes</strong> con el camión
                          <strong> {analisis.division_sugerida[0].camion_sugerido.tipo}</strong>.
                        </span>
                      </div>
                      {analisis.division_sugerida.map(d => (
                        <div key={d.numero} className="pc-excede-viaje-card">
                          <div className="pc-excede-viaje-header">
                            <span className="viaje-num">Viaje {d.numero}</span>
                            <span className="viaje-util">{d.util_peso_pct}% peso · {d.util_m3_pct}% m³</span>
                            {d.viable
                              ? <span className="viaje-badge ok">VIABLE</span>
                              : <span className="viaje-badge warn">FALTAN {formatUSD(d.faltante)}</span>}
                          </div>
                          <div className="pc-excede-viaje-stats">
                            <span>{d.total_notas} nota(s)</span>
                            <span>{formatNumber(d.peso, 2)} kg</span>
                            <span>{formatNumber(d.m3, 3)} m³</span>
                            <span>{formatUSD(d.usd)}</span>
                          </div>
                          <div className="pc-excede-viaje-notas">
                            {d.notas.map(n => (
                              <span key={n.nota_num} className="pc-excede-nota-pill">
                                {n.nota_num}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          <div className="pc-modal-actions">
            <button type="button" onClick={onClose}>Cancelar</button>
            {tab === 'cambiar' ? (
              <button
                type="button" className="primary"
                onClick={handleCambiar}
                disabled={!camionSel || loading}>
                <Truck size={14} /> Usar este camión
              </button>
            ) : (
              <button
                type="button" className="primary"
                onClick={handleDividir}
                disabled={!divisionOk || loading}>
                <Layers size={14} /> Certificar {analisis?.division_sugerida?.length || 0} viajes
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   TARJETA INDIVIDUAL DE PROPUESTA
   ============================================================ */
const PropuestaCard = ({
  propuesta, seleccionada, expandida,
  onToggleSel, onToggleExpandir, onCertificar, onAbrirOpciones,
  rutaColors = {},
}) => {
  const p = propuesta;
  const esMixta = p.ciudades.length > 1;
  const esInsuficiente = !p.supera_minimo;
  const excedeCapacidad = !!p.excede_capacidad;
  const bajoUtilizacion = !!p.bajo_utilizacion;
  const esInterRuta = !!p.es_inter_ruta;
  const rutasInvolucradas = Array.isArray(p.rutas_involucradas) && p.rutas_involucradas.length
    ? p.rutas_involucradas
    : [p.ruta];

  const noCertificable = esInsuficiente || excedeCapacidad;

  const tipoLabel = {
    INDIVIDUAL: 'Individual',
    MIXTA: 'Mixta',
    INSUFICIENTE: 'Insuficiente',
  }[p.tipo] || p.tipo;

  const tipoClass = {
    INDIVIDUAL: 'tipo-individual',
    MIXTA: 'tipo-mixta',
    INSUFICIENTE: 'tipo-insuficiente',
  }[p.tipo] || '';

  const utilPct = p.utilizacion_max_pct || 0;
  const utilTone = utilPct > 90 ? 'red' : utilPct < 70 ? 'amber' : 'green';

  const minPct = p.minimo_usd > 0
    ? Math.min((p.monto_flete_usd / p.minimo_usd) * 100, 100)
    : 100;

  const colorDe = (ruta) => rutaColors[ruta] || p.color || '#64748b';

  return (
    <div className={`pc-propuesta-card ${noCertificable ? 'is-insuficiente' : ''} ${seleccionada ? 'is-selected' : ''}`}>
      <div className="pc-propuesta-header">
        <div className="pc-propuesta-header-left">
          {!noCertificable ? (
            <input
              type="checkbox"
              checked={seleccionada}
              onChange={onToggleSel}
              className="pc-propuesta-check"
            />
          ) : (
            <Ban size={16} className={excedeCapacidad ? 'pc-icon-danger' : 'pc-icon-muted'} />
          )}

          <div className="pc-propuesta-ciudad">
            <strong>{p.ciudad_principal}</strong>
            {esMixta && (
              <span className="pc-propuesta-ciudades-extra">
                + {p.ciudades.filter(c => c !== p.ciudad_principal).join(' · ')}
              </span>
            )}
          </div>

          <BadgeGrupoMarca grupo={p.grupo_marca} />
          <span className={`pc-tipo-badge ${tipoClass}`}>{tipoLabel}</span>

          <div className="pc-rutas-involucradas">
            {rutasInvolucradas.map(r => (
              <span
                key={r}
                className="pc-ruta-badge"
                style={{
                  background: `${colorDe(r)}22`,
                  borderColor: `${colorDe(r)}66`,
                  color: colorDe(r),
                }}
                title={`Ruta: ${r}`}
              >
                <Route size={10} /> {r}
              </span>
            ))}
            {esInterRuta && (
              <span className="pc-inter-ruta-tag" title="Consolidación entre varias rutas">
                <ArrowRightLeft size={10} /> inter-ruta
              </span>
            )}
          </div>
        </div>

        <div className="pc-propuesta-header-right">
          <span className="pc-propuesta-notas-count">{p.total_notas} nota(s)</span>
          {excedeCapacidad ? (
            <span className="pc-propuesta-estado-pill excede" title="Excede el 90% del camión más grande">
              <Scale size={11} /> Excede capacidad
            </span>
          ) : esInsuficiente ? (
            <span className="pc-propuesta-estado-pill insuf" title="El flete no alcanza el mínimo de la ciudad">
              <AlertCircle size={11} /> Faltan {formatUSD(p.faltante_usd)} de flete
            </span>
          ) : (
            <span className="pc-propuesta-estado-pill apto">
              <CheckCircle2 size={11} /> APTO
            </span>
          )}
        </div>
      </div>

      <div className="pc-propuesta-body">
        <div className="pc-propuesta-metricas">
          <div className="pc-metrica">
            <span className="lbl">Bultos</span>
            <strong>{formatNumber(p.total_bultos, 2)}</strong>
          </div>
          <div className="pc-metrica">
            <span className="lbl">Peso</span>
            <strong>{formatNumber(p.total_peso_kg, 2)} kg</strong>
          </div>
          <div className="pc-metrica">
            <span className="lbl">Volumen</span>
            <strong>{formatNumber(p.total_m3, 3)} m³</strong>
          </div>
          <div className="pc-metrica">
            <span className="lbl">Total USD</span>
            <strong>{formatUSD(p.total_usd)}</strong>
          </div>
          <div className="pc-metrica">
            <span className="lbl">Flete {formatNumber(p.porcentaje_flete, 2)}%</span>
            <strong>{formatUSD(p.monto_flete_usd)}</strong>
          </div>
        </div>

        {p.minimo_usd > 0 && (
          <div className={`pc-propuesta-minimo ${p.supera_minimo ? 'ok' : 'falta'}`}>
            <div className="pc-propuesta-minimo-header">
              <span className="pc-minimo-label">
                Flete vs. Mínimo de <strong>{p.ciudad_principal}</strong>
              </span>
              <span className="pc-minimo-numbers">
                <strong>{formatUSD(p.monto_flete_usd)}</strong>
                <span className="pc-minimo-sep">/</span>
                <span>{formatUSD(p.minimo_usd)}</span>
              </span>
              {p.supera_minimo ? (
                <span className="pc-minimo-status ok">
                  <CheckCircle2 size={11} /> Flete supera mínimo
                </span>
              ) : (
                <span className="pc-minimo-status falta">
                  <AlertCircle size={11} /> Faltan {formatUSD(p.faltante_usd)} de flete
                </span>
              )}
            </div>
            <div className="pc-minimo-bar">
              <div
                className={`pc-minimo-bar-fill ${p.supera_minimo ? 'green' : 'red'}`}
                style={{ width: `${minPct}%` }}
              />
              <div
                className="pc-minimo-bar-marker"
                style={{ left: '100%' }}
                title="Mínimo requerido de flete"
              />
            </div>
          </div>
        )}

        {p.camion_sugerido && (
          <div className="pc-propuesta-camion">
            <Truck size={14} />
            <span>
              <strong>{p.camion_sugerido.tipo}</strong>
              {' '}· {formatNumber(p.camion_sugerido.capacidad_kg)} kg / {formatNumber(p.camion_sugerido.capacidad_m3)} m³
            </span>
            <div className="pc-mini-bars">
              <label className="pc-mini-bar-label">Peso</label>
              <div className="pc-mini-bar">
                <div
                  className={`pc-mini-bar-fill ${p.utilizacion_peso_pct > 90 ? 'red' : p.utilizacion_peso_pct < 70 ? 'amber' : 'green'}`}
                  style={{ width: `${Math.min(p.utilizacion_peso_pct, 100)}%` }}
                />
              </div>
              <span className="pc-mini-bar-label">{p.utilizacion_peso_pct}%</span>
              <label className="pc-mini-bar-label">M3</label>
              <div className="pc-mini-bar">
                <div
                  className={`pc-mini-bar-fill ${p.utilizacion_m3_pct > 90 ? 'red' : p.utilizacion_m3_pct < 70 ? 'amber' : 'green'}`}
                  style={{ width: `${Math.min(p.utilizacion_m3_pct, 100)}%` }}
                />
              </div>
              <span className="pc-mini-bar-label">{p.utilizacion_m3_pct}%</span>
            </div>
          </div>
        )}

        {excedeCapacidad ? (
          <div className={`pc-util-warning ${utilTone}`}>
            <AlertTriangle size={14} />
            <span>
              Utilización máxima <strong>{utilPct}%</strong> — supera el límite del 90%.
              Puedes cambiar de camión o dividir en varios viajes.
            </span>
          </div>
        ) : bajoUtilizacion ? (
          <div className="pc-util-info">
            <Info size={13} />
            <span>
              Carga liviana (<strong>{utilPct}%</strong> del camión).
              No hay más notas disponibles para fusionar.
              Apta para despacho.
            </span>
          </div>
        ) : null}
      </div>

      <div className="pc-propuesta-footer">
        <button
          className="pc-btn-expandir"
          onClick={onToggleExpandir}
          disabled={p.notas.length === 0}
        >
          {expandida ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {expandida ? 'Ocultar' : 'Ver'} {p.total_notas} notas
        </button>

        <div style={{ flex: 1 }} />

        {excedeCapacidad ? (
          <button className="pc-btn-resolver" onClick={onAbrirOpciones}>
            <Wand2 size={14} />
            Resolver exceso
            <ChevronRight size={14} />
          </button>
        ) : esInsuficiente ? (
          <span className="pc-propuesta-warn">
            <AlertCircle size={13} />
            El flete no alcanza el mínimo — agregar más notas
          </span>
        ) : (
          <button className="pc-btn-certificar-prop" onClick={onCertificar}>
            <ShieldCheck size={14} />
            Certificar esta propuesta
            <ArrowRightCircle size={14} />
          </button>
        )}
      </div>

      {expandida && (
        <div className="pc-propuesta-notas">
          <div className="pc-propuesta-notas-header">
            <span>Nota</span>
            <span>Cliente</span>
            <span>Ciudad</span>
            <span className="ta-right">Bultos</span>
            <span className="ta-right">USD</span>
          </div>
          {p.notas.map(n => (
            <div className="pc-propuesta-nota-row" key={n.nota_num}>
              <span className="nota-mono">{n.nota_num}</span>
              <span className="nota-cliente" title={n.cliente_principal || n.cliente_nombre}>
                {n.cliente_principal || n.cliente_nombre || '-'}
              </span>
              <span className="nota-ciudad">{n.ciudad || '-'}</span>
              <span className="ta-right nota-num">{formatNumber(n.cantidad, 2)}</span>
              <span className="ta-right nota-usd">{formatUSD(n.total_usd)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   PROPUESTAS DE CONSOLIDACIÓN + FILTROS
   ============================================================ */
const PropuestasConsolidacion = ({
  propuestas, summary, loading, filtros, setFiltros,
  onCertificar, onCertificarLote, onRefresh, onNotify,
}) => {
  const [seleccionadas, setSeleccionadas] = useState(new Set());
  const [expandida, setExpandida] = useState(null);
  const [opcionesExcede, setOpcionesExcede] = useState(null);

  // Filtros locales con MultiSelectFilter
  const [filtroTexto, setFiltroTexto] = useState('');
  const [selectedRutas, setSelectedRutas] = useState([]);
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [selectedTipos, setSelectedTipos] = useState([]);
  const [selectedEstados, setSelectedEstados] = useState([]);

  useEffect(() => {
    setSeleccionadas(new Set());
    setExpandida(null);
  }, [propuestas]);

  const rutaColors = useMemo(() => {
    const map = {};
    Object.entries(summary?.por_ruta || {}).forEach(([r, d]) => {
      map[r] = d.color;
    });
    return map;
  }, [summary]);

  const toggleSel = (id) => {
    setSeleccionadas(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleExpandir = (id) => setExpandida(prev => (prev === id ? null : id));

  const propuestasFiltradas = useMemo(() => {
    return propuestas.filter(p => {
      if (filtroTexto.trim()) {
        const q = filtroTexto.toLowerCase().trim();
        const match =
          String(p.ciudad_principal || '').toLowerCase().includes(q) ||
          (p.ciudades || []).some(c => String(c).toLowerCase().includes(q)) ||
          (p.rutas_involucradas || []).some(r => String(r).toLowerCase().includes(q)) ||
          (p.notas || []).some(n => String(n.nota_num || '').toLowerCase().includes(q));
        if (!match) return false;
      }
      if (selectedRutas.length > 0) {
        const rutasP = p.rutas_involucradas || [p.ruta];
        if (!rutasP.some(r => selectedRutas.includes(r))) return false;
      }
      if (selectedGrupos.length > 0 && !selectedGrupos.includes(p.grupo_marca)) {
        return false;
      }
      if (selectedTipos.length > 0 && !selectedTipos.includes(p.tipo)) {
        return false;
      }
      if (selectedEstados.length > 0) {
        const matchEstado = selectedEstados.some(st => {
          if (st === 'APTO') return p.supera_minimo;
          if (st === 'INSUFICIENTE') return !p.supera_minimo;
          if (st === 'EXCEDE') return p.excede_capacidad;
          if (st === 'LIVIANA') return p.bajo_utilizacion;
          return false;
        });
        if (!matchEstado) return false;
      }
      return true;
    });
  }, [propuestas, filtroTexto, selectedRutas, selectedGrupos, selectedTipos, selectedEstados]);

  const hayFiltros = Boolean(
    filtroTexto.trim() ||
    selectedRutas.length > 0 ||
    selectedGrupos.length > 0 ||
    selectedTipos.length > 0 ||
    selectedEstados.length > 0
  );

  const limpiarFiltros = () => {
    setFiltroTexto('');
    setSelectedRutas([]);
    setSelectedGrupos([]);
    setSelectedTipos([]);
    setSelectedEstados([]);
  };

  const rutasDisponibles = useMemo(() => {
    const set = new Set();
    propuestas.forEach(p => (p.rutas_involucradas || [p.ruta]).forEach(r => r && set.add(r)));
    return Array.from(set).sort();
  }, [propuestas]);

  const gruposDisponibles = useMemo(() => {
    const set = new Set();
    propuestas.forEach(p => p.grupo_marca && set.add(p.grupo_marca));
    return Array.from(set).sort();
  }, [propuestas]);

  const tiposDisponibles = useMemo(() => {
    const set = new Set();
    propuestas.forEach(p => p.tipo && set.add(p.tipo));
    return Array.from(set).sort();
  }, [propuestas]);

  const estadosDisponibles = useMemo(() => [
    { value: 'APTO', label: 'Solo aptas' },
    { value: 'INSUFICIENTE', label: 'Insuficientes' },
    { value: 'EXCEDE', label: 'Exceden capacidad' },
    { value: 'LIVIANA', label: 'Bajo utilización' },
  ], []);

  const seleccionables = propuestasFiltradas.filter(p => p.supera_minimo && !p.excede_capacidad);
  const todasSeleccionadas = seleccionables.length > 0
    && seleccionables.every(p => seleccionadas.has(p.id));

  const toggleTodas = () => {
    if (todasSeleccionadas) setSeleccionadas(new Set());
    else setSeleccionadas(new Set(seleccionables.map(p => p.id)));
  };

  const certificarLote = () => {
    const seleccion = propuestas.filter(p => seleccionadas.has(p.id));
    if (seleccion.length === 0) return;
    onCertificarLote?.(seleccion);
  };

  const nombreMes = filtros.mes ? MESES[Number(filtros.mes) - 1] : 'TODOS LOS MESES';
  const etiquetaRango = filtros.mes && filtros.anio
    ? `${nombreMes} ${filtros.anio}`
    : filtros.anio ? `AÑO ${filtros.anio}` : 'TODOS LOS PERIODOS';

  const porRuta = summary?.por_ruta || {};
  const rutasOrdenadas = Object.entries(porRuta)
    .sort(([, a], [, b]) => (a.ruta_orden || 99) - (b.ruta_orden || 99));

  return (
    <div className="pc-table-card pc-propuestas-card">
      <div className="pc-table-header">
        <div className="pc-table-title">
          <Zap size={16} />
          <h3>Propuestas de Consolidación</h3>
          <span className="pc-count">{propuestasFiltradas.length}</span>
          {hayFiltros && (
            <span className="pc-count-pending">
              {propuestas.length - propuestasFiltradas.length} filtradas
            </span>
          )}

          {rutasOrdenadas.map(([ruta, data]) => (
            <span
              key={ruta}
              className="pc-ruta-chip"
              style={{
                background: `${data.color}22`,
                borderColor: `${data.color}66`,
                color: data.color,
              }}
              title={`${data.aptas} aptas de ${data.total} · ${formatUSD(data.usd)}`}
            >
              <Route size={11} /> {ruta} ({data.total})
            </span>
          ))}

          {summary?.total_inter_ruta > 0 && (
            <span className="pc-stat-chip inter-ruta" title="Propuestas que combinan varias rutas">
              <ArrowRightLeft size={11} /> {summary.total_inter_ruta} inter-ruta
            </span>
          )}

          <span className="pc-periodo-badge">
            <Calendar size={11} /> {etiquetaRango}
          </span>

          {seleccionadas.size > 0 && (
            <span className="pc-count-selected">{seleccionadas.size} seleccionadas</span>
          )}
        </div>

        <div className="pc-table-actions">
          {summary && summary.total_bajo_utilizacion > 0 && (
            <span className="pc-stat-chip amber" title="Propuestas con carga liviana (< 70% del camión)">
              <Percent size={11} /> {summary.total_bajo_utilizacion} livianas
            </span>
          )}
          {summary && summary.total_exceden_capacidad > 0 && (
            <span className="pc-stat-chip red" title="Propuestas que exceden el 90%">
              <Scale size={11} /> {summary.total_exceden_capacidad} exceden
            </span>
          )}
          {summary && summary.usd_total_apto > 0 && (
            <span className="pc-usd-badge" title="Mercancía total · Flete total (solo propuestas aptas)">
              <Package size={11} /> USD {formatUSD(summary.usd_total_apto)}
              {summary.flete_total_apto > 0 && (
                <>
                  {' · '}
                  <TrendingUp size={11} /> {formatUSD(summary.flete_total_apto)}
                </>
              )}
            </span>
          )}
          <button
            className={`pc-toggle-aptos ${filtros.soloAptos ? 'active' : ''}`}
            onClick={() => setFiltros(f => ({ ...f, soloAptos: !f.soloAptos }))}
            title={filtros.soloAptos ? 'Mostrando solo aptas' : 'Mostrando todas'}
          >
            <ListChecks size={13} />
            {filtros.soloAptos ? 'Solo Aptas' : 'Todas'}
          </button>
          <button
            className="pc-btn-sync"
            onClick={onRefresh}
            disabled={loading}
            title="Recalcular propuestas"
          >
            <RefreshCw size={13} className={loading ? 'spin-anim' : ''} />
          </button>
        </div>
      </div>

      {/* Barra de filtros con MultiSelectFilter */}
      <div className="pc-propuestas-filters">
        <div className="pc-propuestas-filters-search">
          <Search size={14} className="pc-search-icon" />
          <input
            type="text"
            placeholder="Buscar ciudad, ruta, nota, cliente o chofer…"
            value={filtroTexto}
            onChange={e => setFiltroTexto(e.target.value)}
          />
          {filtroTexto && (
            <button className="pc-search-clear-btn" onClick={() => setFiltroTexto('')} title="Limpiar búsqueda">
              <X size={13} />
            </button>
          )}
        </div>

        <MultiSelectFilter
          label="Ruta"
          options={rutasDisponibles}
          selected={selectedRutas}
          onChange={setSelectedRutas}
        />

        <MultiSelectFilter
          label="Marca"
          options={gruposDisponibles}
          selected={selectedGrupos}
          onChange={setSelectedGrupos}
        />

        <MultiSelectFilter
          label="Tipo"
          options={tiposDisponibles}
          selected={selectedTipos}
          onChange={setSelectedTipos}
        />

        <MultiSelectFilter
          label="Estado"
          options={estadosDisponibles}
          selected={selectedEstados}
          onChange={setSelectedEstados}
        />

        {hayFiltros && (
          <button className="pc-btn-clear-filters" onClick={limpiarFiltros} title="Limpiar filtros">
            <FilterX size={13} /> Limpiar
          </button>
        )}
      </div>

      {seleccionables.length > 0 && !loading && (
        <div className="pc-propuestas-batch">
          <label>
            <input
              type="checkbox"
              checked={todasSeleccionadas}
              onChange={toggleTodas}
            />
            <span>Seleccionar todas las aptas ({seleccionables.length})</span>
          </label>

          {seleccionadas.size > 0 && (
            <>
              <span className="pc-selected-info">
                <CheckCircle2 size={12} />
                {seleccionadas.size} seleccionada(s)
              </span>
              <button className="pc-btn-clear-selection" onClick={() => setSeleccionadas(new Set())}>
                <X size={12} /> Limpiar
              </button>
              <div style={{ flex: 1 }} />
              <button className="pc-btn-batch-cert" onClick={certificarLote}>
                <ShieldCheck size={13} />
                Certificar {seleccionadas.size} en Lote
              </button>
            </>
          )}
        </div>
      )}

      <div className="pc-table-scroll pc-propuestas-scroll">
        {loading ? (
          <div className="pc-propuestas-empty">
            <RefreshCw size={20} className="spin-anim" />
            <p>Calculando propuestas de consolidación…</p>
          </div>
        ) : propuestas.length === 0 ? (
          <div className="pc-propuestas-empty">
            <Boxes size={32} className="pc-icon-muted" />
            <p>No hay notas pendientes de consolidar en {etiquetaRango}.</p>
            <span>Prueba cambiando el rango de fechas o el filtro de aptas.</span>
          </div>
        ) : propuestasFiltradas.length === 0 ? (
          <div className="pc-propuestas-empty">
            <FilterX size={32} className="pc-icon-muted" />
            <p>Ninguna propuesta coincide con los filtros actuales.</p>
            <button className="pc-btn-clear-filters" onClick={limpiarFiltros}>
              <FilterX size={13} /> Limpiar filtros
            </button>
          </div>
        ) : (
          propuestasFiltradas.map(p => (
            <PropuestaCard
              key={p.id}
              propuesta={p}
              seleccionada={seleccionadas.has(p.id)}
              expandida={expandida === p.id}
              onToggleSel={() => toggleSel(p.id)}
              onToggleExpandir={() => toggleExpandir(p.id)}
              onCertificar={() => onCertificar?.(p)}
              onAbrirOpciones={() => setOpcionesExcede(p)}
              rutaColors={rutaColors}
            />
          ))
        )}
      </div>

      {opcionesExcede && (
        <ModalOpcionesExcede
          show
          propuesta={opcionesExcede}
          onClose={() => setOpcionesExcede(null)}
          onNotify={onNotify}
          onDividir={(props) => {
            onCertificarLote?.(props);
            setOpcionesExcede(null);
          }}
          onCambiarCamion={(overrides) => {
            onCertificar?.(opcionesExcede, overrides);
            setOpcionesExcede(null);
          }}
        />
      )}
    </div>
  );
};

/* ============================================================
   MODAL DE CERTIFICACIÓN
   ============================================================ */
const ModalCertificarDespacho = ({ show, onClose, data, onCertificado, onNotify }) => {
  const [form, setForm] = useState({
    chofer_nombre: '', chofer_cedula: '', placa_camion: '', observaciones: '',
  });
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');

  if (!show || !data) return null;

  const {
    ciudad, camion, totales, minimoUSD, porcentajeFlete, costoFlete, notas,
    grupo_marca, utilizacion_max_pct, ruta, color, rutas_involucradas,
  } = data;

  const rutasMostrar = Array.isArray(rutas_involucradas) && rutas_involucradas.length
    ? rutas_involucradas
    : [ruta];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcesando(true);
    setError('');
    try {
      const payload = {
        notas_nums: notas.map(n => n.nota_num),
        ciudad: ciudad.trim().toUpperCase(),
        camion_id: camion?.id || null,
        camion_tipo: camion?.tipo || 'NO ESPECIFICADO',
        chofer_nombre: form.chofer_nombre.trim(),
        chofer_cedula: form.chofer_cedula.trim(),
        placa_camion: form.placa_camion.trim(),
        observaciones: form.observaciones.trim(),
        creado_por: 'Almacén Central',
      };

      const res = await apiClient.post('/almacen/consolidar', payload);
      if (res.data?.success) {
        onNotify?.('success', res.data.mensaje || `Guía ${res.data.nro_guia} generada`);
        onCertificado?.(res.data.data);
        onClose();
      } else {
        setError(res.data?.detail || 'Error al consolidar');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error en el servidor al consolidar despacho');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="pc-modal-overlay" onClick={onClose}>
      <div className="pc-modal" style={{ maxWidth: 650 }} onClick={e => e.stopPropagation()}>
        <div className="pc-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <ShieldCheck size={20} className="pc-icon-success" />
            <h3>Certificación y Emisión de Guía de Despacho</h3>
            {rutasMostrar.map(r => {
              const c = r === ruta ? (color || '#64748b') : '#64748b';
              return (
                <span
                  key={r}
                  className="pc-ruta-badge"
                  style={{ background: `${c}22`, borderColor: `${c}66`, color: c }}
                >
                  <Route size={10} /> {r}
                </span>
              );
            })}
            <BadgeGrupoMarca grupo={grupo_marca} />
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="pc-modal-body">
          <div className="modal-certificar-grid">
            <div className="certificar-resumen-card">
              <h4>Resumen de Carga</h4>
              <div className="certificar-resumen-row">
                <span>Ciudad Destino:</span>
                <strong>{ciudad}</strong>
              </div>
              <div className="certificar-resumen-row">
                <span>Camión:</span>
                <strong>{camion?.tipo || '—'}</strong>
              </div>
              <div className="certificar-resumen-row">
                <span>Total Notas:</span>
                <strong>{notas.length} notas</strong>
              </div>
              <div className="certificar-resumen-row">
                <span>Bultos / Peso / M³:</span>
                <strong>{formatNumber(totales.bultos)} b. / {formatNumber(totales.peso, 1)} kg / {formatNumber(totales.m3, 2)} m³</strong>
              </div>
              {utilizacion_max_pct > 0 && (
                <div className="certificar-resumen-row">
                  <span>Utilización:</span>
                  <strong>{utilizacion_max_pct}% del camión</strong>
                </div>
              )}
            </div>

            <div className="certificar-resumen-card">
              <h4>Liquidación Financiera</h4>
              <div className="certificar-resumen-row">
                <span>Valor Total Facturas:</span>
                <strong>{formatUSD(totales.usd)}</strong>
              </div>
              <div className="certificar-resumen-row flete-total">
                <span>Flete a cobrar ({porcentajeFlete}%):</span>
                <strong>{formatUSD(costoFlete)}</strong>
              </div>
              <div className="certificar-resumen-row">
                <span>Mínimo de flete requerido:</span>
                <strong>{formatUSD(minimoUSD)}</strong>
              </div>
              <div
                className="certificar-resumen-row"
                style={{
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                  marginTop: 4,
                  paddingTop: 8,
                  color: costoFlete >= minimoUSD ? '#22c55e' : '#ef4444',
                  fontWeight: 700,
                }}
              >
                <span>Flete vs Mínimo:</span>
                <strong>
                  {costoFlete >= minimoUSD
                    ? `✅ Supera por ${formatUSD(costoFlete - minimoUSD)}`
                    : `❌ Faltan ${formatUSD(minimoUSD - costoFlete)}`}
                </strong>
              </div>
            </div>
          </div>

          <div className="pc-form-grid">
            <div className="pc-form-row">
              <label>Nombre del Chofer</label>
              <input type="text" value={form.chofer_nombre}
                onChange={e => setForm(f => ({ ...f, chofer_nombre: e.target.value }))}
                placeholder="Ej. Juan Pérez" />
            </div>
            <div className="pc-form-row">
              <label>Cédula del Chofer</label>
              <input type="text" value={form.chofer_cedula}
                onChange={e => setForm(f => ({ ...f, chofer_cedula: e.target.value }))}
                placeholder="Ej. V-18.456.789" />
            </div>
          </div>

          <div className="pc-form-row">
            <label>Placa del Camión / Vehículo</label>
            <input type="text" value={form.placa_camion}
              onChange={e => setForm(f => ({ ...f, placa_camion: e.target.value }))}
              placeholder="Ej. A89BC4D" />
          </div>

          <div className="pc-form-row">
            <label>Observaciones de Despacho (Opcional)</label>
            <input type="text" value={form.observaciones}
              onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))}
              placeholder="Instrucciones especiales de ruta, entrega o precinto" />
          </div>

          {error && <div className="pc-form-error"><AlertCircle size={14} /> {error}</div>}

          <div className="pc-modal-actions">
            <button type="button" onClick={onClose} disabled={procesando}>Cancelar</button>
            <button type="submit" className="primary" disabled={procesando}>
              {procesando ? 'Emitiendo Guía…' : <><Check size={14} /> Confirmar y Emitir Guía</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ============================================================
   MODAL GUÍA DE DESPACHO
   ============================================================ */
const ModalGuiaDespacho = ({ show, guia, onClose, onNotify, onAnulada, onConfirm }) => {
  if (!show || !guia) return null;

  const notas = Array.isArray(guia.notas) ? guia.notas : [];

  const handlePrint = () => window.print();

  const handleAnular = () => {
    onConfirm?.(
      'Anular Guía de Despacho',
      `¿Deseas anular la guía "${guia.nro_guia}"? Las ${guia.total_notas} notas de entrega volverán al estatus "NO CONSOLIDADO".`,
      async () => {
        try {
          const res = await apiClient.post(`/almacen/guias/${guia.nro_guia}/anular`);
          if (res.data?.success) {
            onNotify?.('success', res.data.detail || 'Guía anulada');
            onAnulada?.();
            onClose();
          }
        } catch (err) {
          onNotify?.('error', err.response?.data?.detail || 'Error al anular guía');
        }
      }
    );
  };

  return (
    <div className="pc-modal-guia-overlay" onClick={onClose}>
      <div className="pc-modal-guia-container" onClick={e => e.stopPropagation()}>
        <div className="pc-modal-guia-toolbar">
          <div className="toolbar-left">
            <FileText size={18} />
            <span>Guía de Despacho: {guia.nro_guia}</span>
          </div>
          <div className="toolbar-right">
            <button className="pc-btn-print" onClick={handlePrint}>
              <Printer size={14} /> Imprimir Guía
            </button>
            {guia.estado !== 'ANULADA' && (
              <button className="pc-btn-anular-guia" onClick={handleAnular}>
                <Trash2 size={14} /> Anular Guía
              </button>
            )}
            <button className="pc-btn-cancel-row" onClick={onClose} style={{ padding: '6px 12px' }}>
              <X size={14} /> Cerrar
            </button>
          </div>
        </div>

        <div className="pc-guia-paper-scroll">
          <div className="pc-guia-paper" id="guia-despacho-print">
            <div className="guia-top-accent" />

            <div className="guia-header-section">
              <div className="guia-brand">
                <img src={DimassiLogo} alt="Distribuidora Dimassi" className="guia-logo" />
                <div className="guia-brand-text">
                  <h2>DISTRIBUIDORA DIMASSI C.A.</h2>
                  <p>SISTEMA DE GESTIÓN Y CONSOLIDACIÓN DE DESPACHOS</p>
                  <p className="guia-brand-rif">RIF: J-00000000-0 · Control de Almacén y Rutas</p>
                </div>
              </div>

              <div className="guia-number-badge">
                <div className="label">GUÍA DE DESPACHO N°</div>
                <div className="number">{guia.nro_guia}</div>
                <div className="date">
                  Fecha Emisión: {formatDate(guia.fecha_emision || guia.created_at)}
                </div>
                <div style={{ marginTop: 6 }}>
                  <span className={`guia-estado-pill ${guia.estado === 'ANULADA' ? 'anulada' : 'certificada'}`}>
                    {guia.estado || 'CERTIFICADO'}
                  </span>
                </div>
              </div>
            </div>

            <div className="guia-meta-grid">
              <div className="guia-meta-item">
                <span className="lbl">Ciudad Destino</span>
                <span className="val">{guia.ciudad_destino}</span>
              </div>
              <div className="guia-meta-item">
                <span className="lbl">Tipo de Camión</span>
                <span className="val">{guia.camion_tipo}</span>
              </div>
              <div className="guia-meta-item">
                <span className="lbl">Placa del Vehículo</span>
                <span className="val">{guia.placa_camion || 'NO ESPECIFICADA'}</span>
              </div>
              <div className="guia-meta-item">
                <span className="lbl">Chofer Asignado</span>
                <span className="val">{guia.chofer_nombre || 'POR ASIGNAR'}</span>
              </div>
              <div className="guia-meta-item">
                <span className="lbl">Cédula del Chofer</span>
                <span className="val">{guia.chofer_cedula || '—'}</span>
              </div>
              <div className="guia-meta-item">
                <span className="lbl">Capacidad Camión</span>
                <span className="val">
                  {formatNumber(guia.capacidad_camion_kg)} kg / {formatNumber(guia.capacidad_camion_m3)} m³
                </span>
              </div>
              <div className="guia-meta-item">
                <span className="lbl">Total de Notas</span>
                <span className="val">{guia.total_notas} documentos</span>
              </div>
              <div className="guia-meta-item">
                <span className="lbl">Emitido Por</span>
                <span className="val">{guia.creado_por || 'Almacén Central'}</span>
              </div>
            </div>

            <div className="guia-resumen-fin-grid">
              <div className="guia-resumen-box">
                <div className="title">Total Bultos</div>
                <div className="amount">{formatNumber(guia.total_bultos, 2)}</div>
              </div>
              <div className="guia-resumen-box">
                <div className="title">Peso y Volumen</div>
                <div className="amount small">
                  {formatNumber(guia.total_peso_kg, 2)} kg / {formatNumber(guia.total_m3, 3)} m³
                </div>
              </div>
              <div className="guia-resumen-box">
                <div className="title">Valor Carga (USD)</div>
                <div className="amount">{formatUSD(guia.total_usd)}</div>
              </div>
              <div className="guia-resumen-box flete">
                <div className="title">Flete ({formatNumber(guia.porcentaje_flete, 2)}%)</div>
                <div className="amount">{formatUSD(guia.monto_flete_usd)}</div>
              </div>
            </div>

            <div className="guia-table-wrapper">
              <h4>Detalle de Notas de Entrega Incluidas ({notas.length})</h4>
              <table className="guia-paper-table">
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Nota #</th>
                    <th>Cliente</th>
                    <th>Dirección de Entrega</th>
                    <th>Marcas</th>
                    <th className="ta-right">Bultos</th>
                    <th className="ta-right">Peso (kg)</th>
                    <th className="ta-right">M³</th>
                    <th className="ta-right">Valor USD</th>
                  </tr>
                </thead>
                <tbody>
                  {notas.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: 16 }}>
                        Sin notas detalladas
                      </td>
                    </tr>
                  ) : (
                    notas.map(n => (
                      <tr key={n.nota_num}>
                        <td className="mono-strong">{n.nota_num}</td>
                        <td className="clamp-2">{n.cliente_principal || n.cliente_nombre || '-'}</td>
                        <td className="clamp-2">{n.direccion_entrega || '-'}</td>
                        <td className="clamp-2">{n.marcas || '-'}</td>
                        <td className="num">{formatNumber(n.cantidad, 2)}</td>
                        <td className="num">{formatNumber(n.peso, 2)}</td>
                        <td className="num">{formatNumber(n.m3, 3)}</td>
                        <td className="num strong">{formatUSD(n.total_usd)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="ta-right label-total">TOTALES CONSOLIDADOS:</td>
                    <td className="num">{formatNumber(guia.total_bultos, 2)}</td>
                    <td className="num">{formatNumber(guia.total_peso_kg, 2)}</td>
                    <td className="num">{formatNumber(guia.total_m3, 3)}</td>
                    <td className="num">{formatUSD(guia.total_usd)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {guia.observaciones && (
              <div className="guia-observaciones">
                <strong>Observaciones:</strong> {guia.observaciones}
              </div>
            )}

            <div className="guia-firmas-section">
              <div className="guia-firma-box">
                <div className="guia-firma-linea" />
                <span className="guia-firma-cargo">Despachador / Almacén</span>
                <span className="guia-firma-sub">Firma y Sello</span>
              </div>
              <div className="guia-firma-box">
                <div className="guia-firma-linea" />
                <span className="guia-firma-cargo">Transportista / Chofer</span>
                <span className="guia-firma-sub">{guia.chofer_nombre || 'Firma y Cédula'}</span>
              </div>
              <div className="guia-firma-box">
                <div className="guia-firma-linea" />
                <span className="guia-firma-cargo">Recepción en Destino</span>
                <span className="guia-firma-sub">Firma y Fecha de Recibido</span>
              </div>
            </div>

            <div className="guia-footer-brand">
              <span>Distribuidora Dimassi C.A.</span>
              <span>Documento generado por el Sistema de Gestión · {guia.nro_guia}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   MODAL HISTORIAL DE GUÍAS
   ============================================================ */
const ModalHistorialGuias = ({ show, onClose, onVerGuia }) => {
  const [guias, setGuias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTexto, setFiltroTexto] = useState('');

  const cargarGuias = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/almacen/guias', { params: { page_size: 100 } });
      if (res.data?.success) setGuias(res.data.data || []);
    } catch (err) {
      console.error('Error al cargar guías:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (show) cargarGuias(); }, [show, cargarGuias]);

  if (!show) return null;

  const filtradas = guias.filter(g => {
    const q = filtroTexto.toLowerCase().trim();
    if (!q) return true;
    return (
      String(g.nro_guia || '').toLowerCase().includes(q) ||
      String(g.ciudad_destino || '').toLowerCase().includes(q) ||
      String(g.chofer_nombre || '').toLowerCase().includes(q) ||
      String(g.camion_tipo || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="pc-modal-overlay" onClick={onClose}>
      <div className="pc-modal" style={{ maxWidth: 860 }} onClick={e => e.stopPropagation()}>
        <div className="pc-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={18} />
            <h3>Historial de Guías de Despacho</h3>
            <span className="pc-count">{filtradas.length}</span>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>

        <div className="pc-search-bar" style={{ margin: '8px 16px', borderRadius: 6 }}>
          <Search size={14} />
          <input type="text"
            placeholder="Buscar por Nro de Guía, Ciudad, Chofer o Camión…"
            value={filtroTexto}
            onChange={e => setFiltroTexto(e.target.value)} />
        </div>

        <div className="pc-table-scroll" style={{ maxHeight: 420, margin: '0 16px 16px' }}>
          <table className="pc-table">
            <thead>
              <tr>
                <th style={{ width: 140 }}>Nro Guía</th>
                <th style={{ width: 90 }}>Fecha</th>
                <th>Ciudad</th>
                <th>Camión</th>
                <th>Chofer</th>
                <th style={{ textAlign: 'right', width: 70 }}>Notas</th>
                <th style={{ textAlign: 'right', width: 100 }}>Total USD</th>
                <th style={{ textAlign: 'right', width: 90 }}>Flete USD</th>
                <th style={{ width: 90 }}>Estado</th>
                <th style={{ width: 70 }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="pc-empty"><RefreshCw size={14} className="spin-anim" /> Cargando guías…</td></tr>
              ) : filtradas.length === 0 ? (
                <tr><td colSpan={10} className="pc-empty">No se encontraron guías de despacho</td></tr>
              ) : filtradas.map(g => (
                <tr key={g.id}>
                  <td className="mono" style={{ color: 'var(--in-table)', fontWeight: 'bold' }}>{g.nro_guia}</td>
                  <td className="muted">{formatDate(g.fecha_emision)}</td>
                  <td><strong>{g.ciudad_destino}</strong></td>
                  <td>{g.camion_tipo}</td>
                  <td>{g.chofer_nombre || '—'}</td>
                  <td className="num">{g.total_notas}</td>
                  <td className="num green">{formatUSD(g.total_usd)}</td>
                  <td className="num amber">{formatUSD(g.monto_flete_usd)}</td>
                  <td>
                    <span className={`guia-estado-pill ${g.estado === 'ANULADA' ? 'anulada' : 'certificada'}`}>
                      {g.estado}
                    </span>
                  </td>
                  <td>
                    <button className="pc-btn-save-row"
                      style={{ padding: '3px 8px', fontSize: '0.7rem' }}
                      onClick={() => onVerGuia(g.nro_guia)}
                      title="Ver o Imprimir Guía">
                      <Eye size={12} /> Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


/* ============================================================
   PÁGINA: CONSOLIDACIÓN DE DESPACHO
   ============================================================ */
const ConsolidacionDespacho = () => {
  const navigate = useNavigate();
  const [propuestas, setPropuestas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [datosCertificar, setDatosCertificar] = useState(null);
  const [guiaActual, setGuiaActual] = useState(null);
  const [showModalGuia, setShowModalGuia] = useState(false);
  const [showHistorialGuias, setShowHistorialGuias] = useState(false);

  const [filtros, setFiltros] = useState({
    mes: MES_ACTUAL,
    anio: ANIO_ACTUAL,
    soloAptos: false,
  });

  const [toast, setToast] = useState({ show: false, type: 'info', message: '' });
  const notify = useCallback((type, message) => setToast({ show: true, type, message }), []);
  const hideToast = useCallback(() => setToast(prev => ({ ...prev, show: false })), []);

  const [confirmDialog, setConfirmDialog] = useState({
    show: false, title: '', message: '', onConfirm: null, loading: false,
  });
  const confirmar = useCallback((title, message, onConfirm) => {
    setConfirmDialog({ show: true, title, message, onConfirm, loading: false });
  }, []);
  const handleConfirm = useCallback(async () => {
    if (!confirmDialog.onConfirm) return;
    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try { await confirmDialog.onConfirm(); }
    finally {
      setConfirmDialog({
        show: false, title: '', message: '', onConfirm: null, loading: false,
      });
    }
  }, [confirmDialog]);

  const fetchPropuestas = useCallback(async (f) => {
    setLoading(true);
    setError('');
    try {
      const params = { solo_aptos: !!f.soloAptos };
      if (f.mes && f.anio) {
        const lastDay = ultimoDiaDelMes(f.anio, f.mes);
        params.fecha_desde = `${f.anio}-${f.mes}-01`;
        params.fecha_hasta = `${f.anio}-${f.mes}-${String(lastDay).padStart(2, '0')}`;
      }
      const res = await apiClient.get('/almacen/consolidacion/propuestas', { params });
      if (res.data?.success) {
        setPropuestas(res.data.data?.propuestas || []);
        setSummary(res.data.data?.summary || null);
      }
    } catch (err) {
      console.error('Error propuestas:', err);
      setError('No se pudieron calcular las propuestas');
      setPropuestas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPropuestas(filtros);
  }, [fetchPropuestas, filtros]);

  const handleVerGuia = async (nroGuia) => {
    try {
      const res = await apiClient.get(`/almacen/guias/${nroGuia}`);
      if (res.data?.success) {
        setGuiaActual(res.data.data);
        setShowModalGuia(true);
      } else {
        notify('error', res.data?.detail || 'No se pudo abrir la guía');
      }
    } catch (err) {
      notify('error', err.response?.data?.detail || 'Error al obtener guía de despacho');
    }
  };

  const handleCertificarPropuesta = (propuesta, overrides = {}) => {
    setDatosCertificar(propuestaToData(propuesta, overrides));
  };

  const handleCertificarLote = (props) => {
    confirmar(
      'Certificar en lote',
      `¿Certificar ${props.length} propuesta(s)? Se generará una guía de despacho por cada una.`,
      async () => {
        try {
          const payload = {
            propuestas: props.map(p => ({
              ciudad_principal: p.ciudad_principal,
              notas_nums: p.notas.map(n => n.nota_num),
              camion_id: p.camion_sugerido?.id || null,
              camion_tipo: p.camion_sugerido?.tipo || 'NO ESPECIFICADO',
              chofer_nombre: '',
              chofer_cedula: '',
              placa_camion: '',
              observaciones: '',
              creado_por: 'Almacén Central',
            })),
          };
          const res = await apiClient.post('/almacen/consolidacion/confirmar-lote', payload);
          if (res.data?.success) {
            const r = res.data.resumen;
            notify(
              r.fallidas === 0 ? 'success' : 'info',
              `${r.certificadas} guía(s) certificada(s)${r.fallidas > 0 ? ` · ${r.fallidas} con error` : ''}`
            );
            fetchPropuestas(filtros);
          }
        } catch (err) {
          notify('error', err.response?.data?.detail || 'Error en certificación masiva');
        }
      }
    );
  };

  return (
    <div className="dashboard-container">
      <DashboardMenuHeader />
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Consolidación de Despacho</h1>
          <div className="title-underline" />
          <p className="dashboard-subtitle" style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
            Simulación, Optimización y Certificación de Rutas y Cargas
          </p>
        </div>
        <div className="date-selectors" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="pc-filter-group">
            <label>Mes</label>
            <select
              value={filtros.mes}
              onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))}>
              {MESES.map((m, i) => (
                <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
              ))}
            </select>
          </div>
          <div className="pc-filter-group">
            <label>Año</label>
            <select
              value={filtros.anio}
              onChange={e => setFiltros(f => ({ ...f, anio: e.target.value }))}>
              {aniosDisponibles().map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <button className="pc-btn-guias-history"
            onClick={() => setShowHistorialGuias(true)}
            title="Ver histórico de guías de despacho">
            <FileText size={15} /> Guías
          </button>
          <button className="refresh-button" onClick={() => fetchPropuestas(filtros)} disabled={loading} title="Refrescar propuestas">
            <RefreshCw size={15} className={loading ? 'spin-anim' : ''} />
          </button>
          <button
            className="pc-nav-link-btn"
            onClick={() => navigate('/panelalmacen')}
            title="Ir al Panel de Flota, Rutas y Tarifario">
            <Settings2 size={15} /> Panel Almacén
          </button>
        </div>
      </header>

      {error && (
        <div className="pc-error-banner">
          <AlertCircle size={14} />
          <span>{error}</span>
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      <PropuestasConsolidacion
        propuestas={propuestas}
        summary={summary}
        loading={loading}
        filtros={filtros}
        setFiltros={setFiltros}
        onCertificar={handleCertificarPropuesta}
        onCertificarLote={handleCertificarLote}
        onRefresh={() => fetchPropuestas(filtros)}
        onNotify={notify}
      />

      {datosCertificar && (
        <ModalCertificarDespacho
          show={!!datosCertificar}
          data={datosCertificar}
          onClose={() => setDatosCertificar(null)}
          onNotify={notify}
          onCertificado={guia => {
            fetchPropuestas(filtros);
            setGuiaActual(guia);
            setShowModalGuia(true);
          }}
        />
      )}

      {showModalGuia && (
        <ModalGuiaDespacho
          show={showModalGuia}
          guia={guiaActual}
          onClose={() => { setShowModalGuia(false); setGuiaActual(null); }}
          onNotify={notify}
          onConfirm={confirmar}
          onAnulada={() => fetchPropuestas(filtros)}
        />
      )}

      {showHistorialGuias && (
        <ModalHistorialGuias
          show={showHistorialGuias}
          onClose={() => setShowHistorialGuias(false)}
          onVerGuia={nro => {
            setShowHistorialGuias(false);
            handleVerGuia(nro);
          }}
        />
      )}

      <Toast show={toast.show} type={toast.type} message={toast.message} onClose={hideToast} />

      <ConfirmDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        loading={confirmDialog.loading}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog({
          show: false, title: '', message: '', onConfirm: null, loading: false,
        })}
      />
    </div>
  );
};

export default ConsolidacionDespacho;
